import { useMemo } from "react";
import type { FacetBucket } from "modules/collections/details/model/collection";

const BAR_COUNT = 32;
const BAR_HEIGHT = 32;

interface FacetDistributionProps {
  buckets: FacetBucket[];
  lowerBound: number;
  upperBound: number;
  activeMin: number;
  activeMax: number;
}

export function FacetDistribution({
  buckets,
  lowerBound,
  upperBound,
  activeMin,
  activeMax,
}: FacetDistributionProps) {
  const bins = useMemo(() => {
    if (!buckets?.length || upperBound <= lowerBound) return [];

    const range = upperBound - lowerBound;
    const binWidth = range / BAR_COUNT;
    const counts = new Array<number>(BAR_COUNT).fill(0);

    for (const b of buckets) {
      const v = Number(b.value);
      if (!Number.isFinite(v)) continue;
      const idx = Math.min(
        Math.floor((v - lowerBound) / binWidth),
        BAR_COUNT - 1
      );
      if (idx >= 0) counts[idx] += b.count;
    }

    const maxCount = Math.max(...counts);
    if (maxCount === 0) return [];

    return counts.map((c) => ({ ratio: c / maxCount, count: c }));
  }, [buckets, lowerBound, upperBound]);

  if (!bins.length) return null;

  return (
    <div
      className="flex items-end gap-px"
      style={{ height: BAR_HEIGHT }}
      aria-hidden
    >
      {bins.map(({ ratio, count }, i) => {
        const binStart =
          lowerBound + (i / BAR_COUNT) * (upperBound - lowerBound);
        const binEnd =
          lowerBound + ((i + 1) / BAR_COUNT) * (upperBound - lowerBound);
        const inRange = binEnd > activeMin && binStart < activeMax;

        return (
          <div
            key={i}
            title={`${count}`}
            className={`flex-1 rounded-t-sm transition-colors ${
              inRange ? "bg-primary/30" : "bg-base-content/8"
            }`}
            style={{
              height: ratio > 0 ? `${Math.max(ratio * 100, 8)}%` : "0%",
            }}
          />
        );
      })}
    </div>
  );
}
